import * as React from 'react'

import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import {
  claimKeyboard,
  KeyboardClaim,
  releaseKeyboard,
} from '~/client/src/shared/utils/keyboardClaim'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'

import { ICalendarDates } from '../../models/CalendarEvent'
import IPermitFieldDto from '../../models/IPermitFieldDto'
import Workflow from '../../models/Workflow'
import CompaniesStore from '../../stores/domain/Companies.store'
import { FileUploadingStore } from '../../stores/domain/FileUploading.store'
import GraphExecutorStore from '../../stores/domain/GraphExecutor.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import MaterialCategoriesStore from '../../stores/domain/MaterialCategories.store'
import MaterialsStore from '../../stores/domain/Materials.store'
import PermitInspectionChangesStore from '../../stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '../../stores/domain/PermitInspections.store'
import PermitStatusChangesStore from '../../stores/domain/PermitStatusChanges.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import RecurringWorkflowSettingsStore from '../../stores/domain/RecurringWorkflowSettings.store'
import SitemapItemsStore from '../../stores/domain/SitemapItems.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import EventsStore from '../../stores/EventStore/Events.store'
import ProjectDateStore from '../../stores/ui/ProjectDate.store'
import CalendarPlaceholderStore from '../CalendarDayView/components/CalendarPlaceholder.store'
import PermitCompactHeaderBar from '../CompactHeaderBar/components/PermitCompactHeaderBar'
import FutureIntervalsRestrictionsDialog from '../FutureIntervalsRestrictionsDialog/FutureIntervalsRestrictionsDialog'
import PermitFormLoader from './components/PermitFormLoader'
import WorkflowSubmissionButtons from './components/WorkflowSubmissionButtons'
import SitePermitCreationFormContent from './SitePermitCreationFormContent'
import SitePermitCreationFormStore from './WorkflowCreationForm.store'

import './SitePermitCreationForm.scss'

// localization: no text to translate

interface IProps {
  isTypeReadOnly?: boolean
  preselectedTypeId?: string
  prepopulatedFields?: IPermitFieldDto[]
  calendarEventDates?: ICalendarDates
  workflowToDuplicate?: Workflow
  FileInputType

  close(): void
  openWorkflow?: (workflow: Workflow) => void

  projectDateStore?: ProjectDateStore
  eventsStore?: EventsStore
  fileUploadingStore?: FileUploadingStore
  workflowTypesStore?: WorkflowTypesStore
  permitStatusChangesStore?: PermitStatusChangesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  calendarPlaceholderStore?: CalendarPlaceholderStore
  locationAttributesStore?: LocationAttributesStore
  materialsStore?: MaterialsStore
  materialCategoryStore?: MaterialCategoriesStore
  tagsStore?: TagsStore
  graphExecutorStore?: GraphExecutorStore
  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  sitemapItemsStore?: SitemapItemsStore
  closuresStore?: ClosuresStore
  markupsStore?: MarkupsStore
}

@inject(
  'projectDateStore',
  'eventsStore',
  'fileUploadingStore',
  'workflowTypesStore',
  'permitStatusChangesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'userProjectsStore',
  'projectMembersStore',
  'companiesStore',
  'locationAttributesStore',
  'materialsStore',
  'materialCategoryStore',
  'calendarPlaceholderStore',
  'tagsStore',
  'graphExecutorStore',
  'recurringWorkflowSettingsStore',
  'sitemapItemsStore',
  'closuresStore',
  'markupsStore',
)
@observer
export default class SitePermitCreationForm extends React.Component<IProps> {
  private readonly store: SitePermitCreationFormStore
  @observable private localProps: IProps = null
  private closeOverlay: (() => void) | null = null
  private keyboardClaim: KeyboardClaim = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)

    this.store = new SitePermitCreationFormStore(
      props.eventsStore,
      props.projectDateStore,
      props.fileUploadingStore,
      props.workflowTypesStore,
      props.userProjectsStore,
      props.projectMembersStore,
      props.companiesStore,
      props.locationAttributesStore,
      props.materialsStore,
      props.materialCategoryStore,
      props.tagsStore,
      props.graphExecutorStore,
      props.closuresStore,
      props.markupsStore,
      null,
      props.permitStatusChangesStore,
      props.permitInspectionsStore,
      props.permitInspectionChangesStore,
      props.calendarPlaceholderStore,
      props.recurringWorkflowSettingsStore,
      props.sitemapItemsStore,
    )

    this.store.resetSelectedField()
    this.store.setDefaultSitePermit(
      null,
      props.preselectedTypeId,
      false,
      props.prepopulatedFields,
    )
    this.store.applyAdoptedCarryPlan()
    this.store.workflowFieldsStore.setDuplicateWorkflowData(
      props.workflowToDuplicate,
    )
    this.store.setOpenWorkflowHandler(props.openWorkflow)
  }

  public componentDidMount() {
    if (this.props.calendarEventDates) {
      this.store.applyDates(
        this.props.calendarEventDates.startDate,
        this.props.calendarEventDates.endDate,
      )
    }
    this.store.resnapSeededPin()

    this.store.openDeadlineModalIfNeed()
    this.closeOverlay = notifyOverlayOpen()
    // Covers the map, so it owns the keyboard — see WorkflowViewFormWrapper.
    this.keyboardClaim = claimKeyboard()
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
    this.closeOverlay = null
    releaseKeyboard(this.keyboardClaim)
    this.keyboardClaim = null
    this.store.discardPendingMarkupsIfAbandoned()
  }

  public componentDidUpdate(prevProps: IProps) {
    this.localProps = this.props
    const {
      preselectedTypeId,
      prepopulatedFields,
      calendarEventDates,
      workflowToDuplicate,
    } = this.props

    if (
      preselectedTypeId !== prevProps.preselectedTypeId ||
      prepopulatedFields !== prevProps.prepopulatedFields
    ) {
      this.store.resetSelectedField()
      this.store.setDefaultSitePermit(
        null,
        preselectedTypeId,
        false,
        prepopulatedFields,
      )
    }

    if (workflowToDuplicate !== prevProps.workflowToDuplicate) {
      this.store.workflowFieldsStore.setDuplicateWorkflowData(
        workflowToDuplicate,
      )
    }

    if (prevProps.calendarEventDates !== calendarEventDates) {
      this.store.applyDates(
        calendarEventDates.startDate,
        calendarEventDates.endDate,
      )
    }
  }

  public render() {
    const { editablePermit, template, canSaveWorkflow, isDraftButtonEnabled } =
      this.store
    const {
      close,
      FileInputType,
      workflowTypesStore,
      isTypeReadOnly,
      openWorkflow,
    } = this.props

    return (
      <div className="site-permit-creation-form full-height relative">
        <div className="site-permit-creation-form-content full-height col">
          <PermitCompactHeaderBar
            permit={editablePermit}
            workflowTypesStore={workflowTypesStore}
            onCloseClick={close}
            openWorkflow={openWorkflow}
          />
          <div className="full-height col overflow-hidden relative">
            <PermitFormLoader permitCreationStore={this.store} />
            <SitePermitCreationFormContent
              store={this.store}
              isReadonly={false}
              FileInputType={FileInputType}
              shouldHideResponsible
              onPermitClose={close}
              isTypeReadOnly={isTypeReadOnly}
            />
            <FutureIntervalsRestrictionsDialog
              isShown={this.store.isFutureIntervalsRestrictionsWarningShown}
              intervals={this.store.futureInvalidIntervals}
              onHide={this.store.closeFutureIntervalsRestrictionsWarning}
              onApply={this.store.closeFutureIntervalsRestrictionsWarning}
              formatInterval={(start, end) =>
                this.localProps.projectDateStore.getFullDateIntervalToDisplay(
                  start,
                  end,
                )
              }
            />
            {template && (
              <WorkflowSubmissionButtons
                isDraftBtnEnabled={isDraftButtonEnabled}
                isSubmitBtnEnabled={canSaveWorkflow}
                onDraftClick={this.createDraftWorkflow}
                onSubmitClick={this.createPermit}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  private createPermit = () => {
    const { canSaveWorkflow, createPermit } = this.store
    if (canSaveWorkflow) {
      createPermit(this.props.close)
    }
  }

  private createDraftWorkflow = () => {
    this.store.saveDraftWorkflow(this.props.close)
  }
}
